import {
  AfterViewInit,
  Component,
  computed,
  ContentChild,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MapMarker } from '@angular/google-maps';
import { timer } from 'rxjs';
import { take } from 'rxjs/operators';

import { MapMarkerInfoWindowService } from '../../../services/map-marker-info-window.service';
import { QPointMapsLoader } from '../../../services/maps-loader.service';
import { HasPosition } from '../has-position';
import { InternalMapService } from '../internal-map.service';
import { MapMarkerLabelDirective } from './map-marker-label.directive';

@Component({
  selector: 'qpoint-map-marker',
  template: '' +
    '<ng-template #templateForInfoWindow><ng-content></ng-content></ng-template>',
  standalone: true,
})
export class MapMarkerComponent implements HasPosition, OnChanges, AfterViewInit, OnDestroy {
  private mapMarkerInfoWindowService = inject(MapMarkerInfoWindowService);
  private viewContainerRef = inject(ViewContainerRef);
  public googleMapsMarkerInstance!: MapMarker;
  public hasInfoWindowTemplate = false;
  public options = signal<google.maps.MarkerOptions>({});

  position = computed(() => {
    return this.options()?.position;
  });

  infoWindowOptions = computed(() => {
    return {
      ...this.options(),
      headerDisabled: true
    };
  });

  isInfoWindowOpen = computed(() => {
    return this.mapMarkerInfoWindowService.isInfoWindowOpen(this.markerId) ?? false;
  });

  constructor(private mapService: InternalMapService, private mapLoader: QPointMapsLoader) {
  }

  ngAfterViewInit(): void {
    this.checkIfTemplateEmpty();
  }

  @ViewChild('templateForInfoWindow', { read: TemplateRef })
  public templateRef: TemplateRef<any>;

  @ContentChild(MapMarkerLabelDirective, { read: TemplateRef })
  public labelTemplateRef: TemplateRef<any>;

  @ContentChild(MapMarkerLabelDirective)
  public labelTemplateDirective: MapMarkerLabelDirective;

  @Input('options')
  public set optionsSetter(options: google.maps.MarkerOptions) {
    if (JSON.stringify(this.options()) !== JSON.stringify(options)) {
      this.options.set(options);
    }
  }

  @Input()
  public fitToBounds = true;

  @Input()
  public markerId: string | number = crypto.randomUUID();

  @Input()
  public mapViewContainerRef: ViewContainerRef;

  @Output()
  public mapDragend: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public mapMouseover: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public mapMouseout: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public mapClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public mapLongClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public mapMarkerRightClick: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public optionsUpdated = new EventEmitter<void>();

  @Output()
  public mapDragStart: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  @Output()
  public infoWindowOpened: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public infoWindowClosed: EventEmitter<void> = new EventEmitter<void>();

  ngOnChanges(changes: SimpleChanges): void {
    this.mapLoader.mapsApiLoaded$.pipe(take(1)).subscribe(() => {
      if (changes.options) {
        const value = changes.options.currentValue as google.maps.MarkerOptions;
        if (value.icon && !(typeof value.icon === 'string') && !value.icon.labelOrigin) {
          value.icon.labelOrigin = new google.maps.Point(60, 10);
        }
        timer(10).subscribe(timerValue => {
          this.optionsUpdated.next();
        });
      }
      this.mapService.markerChanged.next();
    });

  }

  ngOnDestroy() {
    if (this.isInfoWindowOpen()) {
      this.closeInfoWindow();
    }

    this.mapMarkerInfoWindowService.closeInfoWindow(this.markerId);
  }

  shouldFitToBounds(): boolean {
    return this.fitToBounds;
  }

  checkIfTemplateEmpty() {
    this.viewContainerRef.clear(); // Clear previous renderings if any
    const embeddedView = this.viewContainerRef.createEmbeddedView(this.templateRef);
    embeddedView.detectChanges(); // Trigger change detection

    // Filter out comment nodes and count only elements and text nodes
    const renderedNodes = embeddedView.rootNodes.filter(node =>
      node.nodeType !== Node.COMMENT_NODE && !(node.nodeType === Node.ELEMENT_NODE && node.tagName === 'NG-TEMPLATE')
    );

    const isEmpty = renderedNodes.length === 0;
    this.viewContainerRef.clear(); // Clean up after checking
    this.hasInfoWindowTemplate = !isEmpty;
  }

  registerGoogleMapsMarkerInstance(googleMapsMarkerInstance: MapMarker) {
    this.googleMapsMarkerInstance = googleMapsMarkerInstance;
  }

  openInfoWindow(closeOthers = true) {
    this.mapMarkerInfoWindowService.openInfoWindow(this, closeOthers);
  }

  closeInfoWindow() {
    this.mapMarkerInfoWindowService.closeInfoWindow(this.markerId);
  }
}
